import { getDatabase, ref, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { createValueWithMetadata } from '../types/implementations';
import { ValueStatus } from '../types/protocols';
import { ComponentFieldPatch } from './componentFieldsApi';
import { recordActivity } from './activityApi';

class ComponentWarrantyApi {
    private db = getDatabase();

    async setWarrantyStartDate(
        buildingKey: string,
        componentKey: string,
        date: string | null
    ): Promise<ComponentFieldPatch> {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Du måste vara inloggad');

        const warrantyRef = ref(
            this.db,
            `buildings/${buildingKey}/structure/components/${componentKey}/warrantyStartDate`
        );

        if (date) {
            // Validate date format
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(date)) {
                throw new Error('Ange datum i format ÅÅÅÅ-MM-DD');
            }

            const year = parseInt(date.substring(0, 4), 10);
            if (year < 1900 || year > 2100) {
                throw new Error('Ange ett giltigt år mellan 1900 och 2100');
            }

            const data = createValueWithMetadata(date, user.uid);
            await set(warrantyRef, data);
            recordActivity('component.updated', { buildingKey, targetKey: componentKey });
            return { warrantyStartDate: data };
        } else {
            // Unset ValueWithMetadata instead of node removal so the
            // change-log trigger can attribute the removal (matches iOS)
            const data = createValueWithMetadata(null, user.uid, ValueStatus.notSet);
            await set(warrantyRef, data);
            recordActivity('component.updated', { buildingKey, targetKey: componentKey });
            return { warrantyStartDate: data };
        }
    }

    async setWarrantyDuration(
        buildingKey: string,
        componentKey: string,
        years: number | null
    ): Promise<ComponentFieldPatch> {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Du måste vara inloggad');

        const warrantyRef = ref(
            this.db,
            `buildings/${buildingKey}/structure/components/${componentKey}/warrantyDurationYears`
        );

        if (years !== null) {
            if (isNaN(years) || years < 1 || years > 99) {
                throw new Error('Ange en giltig garantitid (1-99 år)');
            }

            const data = createValueWithMetadata(years, user.uid);
            await set(warrantyRef, data);
            recordActivity('component.updated', { buildingKey, targetKey: componentKey });
            return { warrantyDurationYears: data };
        } else {
            // Unset ValueWithMetadata instead of node removal so the
            // change-log trigger can attribute the removal (matches iOS)
            const data = createValueWithMetadata(null, user.uid, ValueStatus.notSet);
            await set(warrantyRef, data);
            recordActivity('component.updated', { buildingKey, targetKey: componentKey });
            return { warrantyDurationYears: data };
        }
    }
}

export const componentWarrantyApi = new ComponentWarrantyApi();
